import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Upload,
  Camera,
  Settings,
  Image as ImageIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["General", "Beauty", "Fashion", "Amazon Product Images", "Consumables", "Home"];

export default function PhotoshootAdmin() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ credit_cost: 2 });
  const [savingConfig, setSavingConfig] = useState(false);

  // Form state
  const [formName, setFormName] = useState("");
  const [formCategory, setFormCategory] = useState("General");
  const [formPrompt, setFormPrompt] = useState("");
  const [formImageUrl, setFormImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard");
      return;
    }
    fetchTemplates();
    fetchConfig();
  }, [isAdmin]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/photoshoot/templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/photoshoot/config`, { withCredentials: true });
      setConfig(res.data);
    } catch {
      // config will use defaults
    }
  };

  const openCreate = () => {
    setEditingTemplate(null);
    setFormName("");
    setFormCategory("General");
    setFormPrompt("");
    setFormImageUrl("");
    setShowDialog(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setFormName(t.name);
    setFormCategory(t.category);
    setFormPrompt(t.prompt_template);
    setFormImageUrl(t.image_url || "");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) return toast.error("Name is required");
    if (!formPrompt.trim()) return toast.error("Prompt template is required");

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/api/photoshoot/templates/${editingTemplate.id}`, {
          name: formName,
          category: formCategory,
          prompt_template: formPrompt,
          image_url: formImageUrl,
        }, { withCredentials: true });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/api/photoshoot/templates`, {
          name: formName,
          category: formCategory,
          prompt_template: formPrompt,
          image_url: formImageUrl,
        }, { withCredentials: true });
        toast.success("Template created");
      }
      setShowDialog(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/api/photoshoot/templates/${id}`, { withCredentials: true });
      toast.success("Template deleted");
      fetchTemplates();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleImageUpload = async (e, templateId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);

    try {
      if (templateId) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(
          `${API}/api/photoshoot/templates/${templateId}/upload-image`,
          formData,
          { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
        );
        setFormImageUrl(res.data.image_url);
        toast.success("Image uploaded");
        fetchTemplates();
      } else {
        // For new template - upload as product image then use URL
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/api/photoshoot/upload-product-image`, formData, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFormImageUrl(res.data.image_url);
        toast.success("Image uploaded");
      }
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/api/photoshoot/config`, {
        credit_cost: config.credit_cost,
      }, { withCredentials: true });
      toast.success("Config saved");
    } catch {
      toast.error("Failed to save config");
    } finally {
      setSavingConfig(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="photoshoot-admin">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/admin")} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Camera className="w-5 h-5 text-primary" />
        <h1 className="text-2xl font-semibold">Photoshoot Templates</h1>
      </div>

      {/* Config section */}
      <div className="bg-card rounded-xl border p-4 mb-6 flex items-center gap-4">
        <Settings className="w-4 h-4 text-muted-foreground" />
        <Label className="text-sm font-medium whitespace-nowrap">Credit Cost per Generation:</Label>
        <Input
          type="number"
          min="0"
          step="0.5"
          value={config.credit_cost}
          onChange={(e) => setConfig({ ...config, credit_cost: parseFloat(e.target.value) || 0 })}
          className="w-24"
          data-testid="config-credit-cost"
        />
        <Button
          size="sm"
          onClick={handleSaveConfig}
          disabled={savingConfig}
          data-testid="save-config-btn"
        >
          {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>

      {/* Add button */}
      <div className="flex justify-end mb-4">
        <Button onClick={openCreate} className="gap-2" data-testid="add-template-btn">
          <Plus className="w-4 h-4" /> Add Template
        </Button>
      </div>

      {/* Templates table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="bg-card rounded-xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Image</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="hidden md:table-cell">Prompt</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>
                    {t.image_url ? (
                      <img src={t.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                      {t.category}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                      {t.prompt_template}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEdit(t)}
                        className="p-1.5 rounded-md hover:bg-muted transition-colors"
                        data-testid={`edit-template-${t.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => handleDelete(t.id)}
                        className="p-1.5 rounded-md hover:bg-red-50 transition-colors"
                        data-testid={`delete-template-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {templates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No templates yet. Add one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Template" : "Create Template"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>Template Name</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Studio, Floating, In Use"
                data-testid="template-name-input"
              />
            </div>

            <div>
              <Label>Category</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger data-testid="template-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Prompt Template</Label>
              <Textarea
                value={formPrompt}
                onChange={(e) => setFormPrompt(e.target.value)}
                placeholder="Describe the photoshoot style. This prompt will be sent to the AI with the user's product image."
                className="min-h-[100px]"
                data-testid="template-prompt-input"
              />
            </div>

            <div>
              <Label>Preview Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {formImageUrl ? (
                  <img src={formImageUrl} alt="" className="w-16 h-16 rounded-lg object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="gap-1"
                    data-testid="upload-preview-btn"
                  >
                    {uploadingImage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, editingTemplate?.id)}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
